import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export function Hx8Logo({ size = 40 }) {
  const b = Math.max(2, Math.round(size * 0.06));
  const cs = size * 0.2;
  const corners = [
    { top: -b, left: -b },
    { top: -b, right: -b },
    { bottom: -b, left: -b },
    { bottom: -b, right: -b },
  ];
  return (
    <div style={{
      width: size, height: size, position: 'relative', flexShrink: 0,
      border: `${b}px solid #e8701a`,
      boxShadow: '0 0 18px rgba(232,112,26,0.35), inset 0 0 14px rgba(232,112,26,0.08)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {corners.map((pos, i) => (
        <div key={i} style={{
          position: 'absolute', width: cs, height: cs,
          background: '#e8701a', opacity: 0.7, ...pos
        }} />
      ))}
      <span style={{
        fontFamily: "'DM Mono', monospace",
        fontWeight: 500,
        fontSize: size * 0.28,
        letterSpacing: '-0.01em',
        color: '#e8701a',
        userSelect: 'none',
        lineHeight: 1,
        position: 'relative', zIndex: 1
      }}>Hx8</span>
    </div>
  );
}

const Navigation = ({ variant = 'home' }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const links = variant === 'talent' ? [
    { href: '#mission', label: 'The Mission' },
    { href: '#how-it-works', label: 'How It Works' },
    { href: '#skill-development', label: 'Skills' },
  ] : [
    { href: '#features', label: 'Platform' },
    { href: '#talent', label: 'Talent' },
    { href: '#how-it-works', label: 'How It Works' },
    { href: '#pricing', label: 'Pricing' },
  ];

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
      height: 64, padding: '0 48px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: scrolled ? 'rgba(8,8,7,0.92)' : 'transparent',
      backdropFilter: scrolled ? 'blur(20px)' : 'none',
      borderBottom: scrolled ? '1px solid rgba(240,236,228,0.07)' : '1px solid transparent',
      transition: 'all 0.35s ease',
    }}>
      {/* Logo */}
      <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 14, textDecoration: 'none' }}>
        <Hx8Logo size={34} />
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'rgba(240,236,228,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>hirex8.com</span>
      </Link>

      {/* Desktop Links */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 36 }} className="hidden md:flex">
        {links.map(l => (
          <a key={l.label} href={l.href} style={{
            color: 'rgba(240,236,228,0.5)', textDecoration: 'none',
            fontSize: 14, fontWeight: 500, letterSpacing: '0.02em', transition: 'color 0.2s'
          }}
            onMouseEnter={e => e.target.style.color = '#f0ece4'}
            onMouseLeave={e => e.target.style.color = 'rgba(240,236,228,0.5)'}
          >{l.label}</a>
        ))}
        {variant !== 'talent' && (
          <Link to="/talent" style={{
            color: 'rgba(240,236,228,0.5)', textDecoration: 'none',
            fontSize: 14, fontWeight: 500, letterSpacing: '0.02em', transition: 'color 0.2s'
          }}
            onMouseEnter={e => e.currentTarget.style.color = '#f0ece4'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(240,236,228,0.5)'}
          >Join as Talent</Link>
        )}
      </div>

      {/* Desktop CTAs */}
      <div className="hidden md:flex" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <a href="https://vibe.hirex8.com" style={{
          color: 'rgba(240,236,228,0.5)', fontSize: 13, textDecoration: 'none', fontWeight: 500,
          transition: 'color 0.2s'
        }}
          onMouseEnter={e => e.target.style.color = '#f0ece4'}
          onMouseLeave={e => e.target.style.color = 'rgba(240,236,228,0.5)'}
        >Log in</a>
        <a href="https://vibe.hirex8.com/register?type=client" target="_blank" rel="noopener noreferrer"
          style={{
            background: '#e8701a', color: '#0a0804', border: 'none', borderRadius: 0,
            padding: '9px 20px', fontSize: 12, fontWeight: 700, letterSpacing: '0.07em',
            textTransform: 'uppercase', cursor: 'pointer', textDecoration: 'none',
            fontFamily: "'Space Grotesk', sans-serif", display: 'inline-block',
            clipPath: 'polygon(0 0,calc(100% - 7px) 0,100% 7px,100% 100%,7px 100%,0 calc(100% - 7px))',
            boxShadow: '0 0 18px rgba(232,112,26,0.3)', transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f07d2e'; e.currentTarget.style.boxShadow = '0 0 28px rgba(232,112,26,0.5)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#e8701a'; e.currentTarget.style.boxShadow = '0 0 18px rgba(232,112,26,0.3)'; }}
        >Get Started</a>
      </div>

      {/* Mobile toggle */}
      <button className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f0ece4', padding: 8 }}>
        {mobileOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{
          position: 'absolute', top: 64, left: 0, right: 0,
          background: 'rgba(8,8,7,0.97)', backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(240,236,228,0.07)',
          padding: '24px 48px', display: 'flex', flexDirection: 'column', gap: 20
        }}>
          {links.map(l => (
            <a key={l.label} href={l.href} onClick={() => setMobileOpen(false)}
              style={{ color: 'rgba(240,236,228,0.6)', textDecoration: 'none', fontSize: 15, fontWeight: 500 }}>
              {l.label}
            </a>
          ))}
          <a href="https://vibe.hirex8.com/register?type=client" target="_blank" rel="noopener noreferrer"
            style={{
              background: '#e8701a', color: '#0a0804', padding: '12px 24px',
              fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
              textDecoration: 'none', fontSize: 13, display: 'inline-block',
              clipPath: 'polygon(0 0,calc(100% - 8px) 0,100% 8px,100% 100%,8px 100%,0 calc(100% - 8px))',
            }}
            onClick={() => setMobileOpen(false)}
          >Get Started</a>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
